import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils";
import { X, File } from "lucide-react";
import "./ImagePlaceholderComponent.scss";

interface ImagePlaceholderComponentProps {
    src?: string;
    file?: any;
    alt?: string;
    className?: string;
    size?: "sm" | "md" | "lg";
    showRemoveButton?: boolean;
    onRemove?: () => void;
    onClick?: () => void;
}

const ImagePlaceholderComponent = ({
    src,
    alt = "preview",
    className,
    file,
    size = "md",
    showRemoveButton = true,
    onRemove,
    onClick,
}: ImagePlaceholderComponentProps) => {
    const sizeClasses = {
        sm: "size-6",
        md: "size-8",
        lg: "size-12",
    };

    const removeButtonSizeClasses = {
        sm: "w-3 h-3",
        md: "w-4 h-4",
        lg: "w-5 h-5",
    };

    const content_type = file?.content_type || 'image/png';
    
    // Check if the file is an image type
    const isImage = content_type.startsWith('image/');
    
    return (
        <div className={cn("image-placeholder-container relative group", className)}>
            {showRemoveButton && onRemove && (
                <Button
                    className={cn(
                        "absolute -top-2 -right-2 flex items-center justify-center p-0.5 z-10",
                        removeButtonSizeClasses[size]
                    )}
                    onClick={onRemove}
                    variant="destructive"
                    size="sm"
                    title="Remove file"
                >
                    <X className={cn("w-3 h-3", {
                        "w-2 h-2": size === "sm",
                        "w-4 h-4": size === "lg",
                    })} />
                </Button>
            )}

            <div 
                className={cn("flex items-center", onClick && "cursor-pointer hover:opacity-80 transition-opacity")}
                onClick={onClick}
            >
                {src && isImage ? (
                    <img
                        src={src}
                        alt={alt}
                        className={cn(
                            "object-cover rounded",
                            sizeClasses[size]
                        )}
                    />
                ) : (
                    <div
                        className={cn(
                            "text-gray-500 rounded-md  dark:text-neutral-500 flex items-center justify-center bg-gray-50 dark:bg-gray-800",
                            sizeClasses[size]
                        )}
                    >
                        <File className={cn("text-gray-500 dark:text-gray-400", {
                            "w-3 h-3": size === "sm",
                            "w-4 h-4": size === "md", 
                            "w-6 h-6": size === "lg",
                        })} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImagePlaceholderComponent;
